import * as Sentry from "@sentry/react";
import { maskTokensInUrl, scrubObject, scrubHeaders } from "./sentryPrivacy";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || "https://9f9f730c5d110bd2960f40f652c4ecc5@o4511034116931584.ingest.de.sentry.io/4511034122829904";

/**
 * Map a pathname to a coarse "app area" tag. Privacy-safe: never contains
 * IDs, slugs, query strings, names, emails, etc. Lets us slice Sentry by
 * surface (public vs portal vs admin) without sending PII.
 */
export function areaForPath(pathname: string): string {
  if (pathname.startsWith("/portal/admin") || pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/portal/installer")) return "installer";
  if (pathname.startsWith("/portal/introducer")) return "introducer";
  if (pathname.startsWith("/portal/surveyor")) return "surveyor";
  if (pathname.startsWith("/portal/consultant")) return "consultant";
  if (pathname.startsWith("/portal")) return "portal";
  if (pathname.startsWith("/customer")) return "customer";
  return "public";
}

/** Returns true once Sentry has been successfully initialised. */
let _sentryReady = false;
export function isSentryReady(): boolean {
  return _sentryReady;
}

/**
 * Initialise Sentry. Safe to call even if DSN is not set — it will no-op.
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    if (import.meta.env.DEV) {
      console.info("[Sentry] No VITE_SENTRY_DSN set — Sentry disabled.");
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    sendDefaultPii: false,
    environment: import.meta.env.MODE || "production",
    release: (import.meta.env.VITE_APP_RELEASE as string | undefined) || undefined,
    // Keep performance overhead minimal
    tracesSampleRate: import.meta.env.PROD ? 0.05 : 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Reduce duplicate noise from common boot churn
    sampleRate: import.meta.env.PROD ? 0.5 : 1.0,

    initialScope: {
      tags: {
        area: typeof window !== "undefined" ? areaForPath(window.location.pathname) : "public",
      },
    },

    // Strip PII from all events before they leave the browser
    beforeSend(event, hint) {
      // Drop events whose original error message matches our noise list — Sentry's
      // built-in `ignoreErrors` only checks `event.message`, but real React/runtime
      // errors often surface via `hint.originalException`.
      const original = (hint?.originalException as any);
      const originalMsg: string =
        (typeof original === "string" ? original : original?.message) || event.message || "";
      if (originalMsg && SHOULD_IGNORE_PATTERNS.some((p) =>
        typeof p === "string" ? originalMsg.includes(p) : p.test(originalMsg)
      )) {
        return null;
      }

      // Mask tokens in the event URL
      if (event.request?.url) {
        event.request.url = maskTokensInUrl(event.request.url);
      }

      // Remove denied headers
      if (event.request?.headers) {
        event.request.headers = scrubHeaders(event.request.headers);
      }

      // Remove cookies entirely
      if (event.request?.cookies) {
        event.request.cookies = {};
      }

      // Remove request body (never send form payloads)
      if (event.request?.data) {
        event.request.data = "[REDACTED]";
      }

      // Strip user identifiers — keep role tag only (set explicitly elsewhere).
      if (event.user) {
        event.user = undefined;
      }

      if (event.message) event.message = maskTokensInUrl(event.message);
      for (const exception of event.exception?.values ?? []) {
        if (exception.value) exception.value = maskTokensInUrl(exception.value);
      }

      // Scrub breadcrumb data
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((bc) => ({
          ...bc,
          data: scrubObject(bc.data as Record<string, any>),
          message: bc.message ? maskTokensInUrl(bc.message) : bc.message,
        }));
      }

      // Scrub extra context
      if (event.extra) {
        event.extra = scrubObject(event.extra as Record<string, any>);
      }

      return event;
    },

    // Also scrub breadcrumbs as they're created
    beforeBreadcrumb(breadcrumb) {
      // Mask tokens in navigation breadcrumbs
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = maskTokensInUrl(breadcrumb.data.url);
      }
      if (breadcrumb.data?.from) {
        breadcrumb.data.from = maskTokensInUrl(breadcrumb.data.from);
      }
      if (breadcrumb.data?.to) {
        breadcrumb.data.to = maskTokensInUrl(breadcrumb.data.to);
      }

      // Drop XHR/fetch breadcrumbs that contain auth headers
      if (
        breadcrumb.category === "xhr" ||
        breadcrumb.category === "fetch"
      ) {
        if (breadcrumb.data?.url) {
          breadcrumb.data.url = maskTokensInUrl(breadcrumb.data.url);
        }
        // Remove request/response bodies
        delete breadcrumb.data?.body;
        delete breadcrumb.data?.response;

        // Drop noisy 4xx/successful network breadcrumbs — keep only server errors
        const status = (breadcrumb.data as any)?.status_code;
        if (typeof status === "number" && status < 500) {
          return null;
        }
      }

      // Demote analytics console errors to breadcrumbs only (no event)
      if (
        breadcrumb.category === "console" &&
        breadcrumb.level === "error" &&
        typeof breadcrumb.message === "string" &&
        /analytics|gtag|clarity|fbq|meta pixel/i.test(breadcrumb.message)
      ) {
        breadcrumb.level = "warning";
      }

      return breadcrumb;
    },

    // Ignore common noise (string/regex match against event.message)
    ignoreErrors: SHOULD_IGNORE_PATTERNS,

    // Ignore known-noisy URLs (browser extensions, ad-blocker injectors)
    denyUrls: [
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      /safari-extension:\/\//i,
      /^webkit-masked-url:/i,
      // Well-known third-party noise
      /googletagmanager\.com/i,
      /clarity\.ms/i,
      /connect\.facebook\.net/i,
    ],
  });

  _sentryReady = true;
}

/**
 * Common noise patterns (browser extensions, transient network blips,
 * resize observer churn, lazy-chunk reloads).
 */
const SHOULD_IGNORE_PATTERNS: (string | RegExp)[] = [
  "ResizeObserver loop",
  "Non-Error promise rejection",
  "Load failed",
  "Failed to fetch",
  "NetworkError",
  "AbortError",
  "ChunkLoadError",
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk/i,
  /^Script error\.?$/,
  /__gCrWeb/i,                    // Chrome iOS extensions
  /webkitExitFullScreen/i,
  /atomicFindClose/i,
  /can't redefine non-configurable property "userAgent"/i, // bot-blocker injection
  /Object Not Found Matching Id/i, // Outlook safe links
  /the operation was aborted/i,
  /TypeError: Failed to fetch dynamically imported module/i,
];

/**
 * Tag the current Sentry scope with a coarse app area + role.
 * Safe to call repeatedly; no-op if Sentry not initialised.
 */
export function setSentryArea(area: string): void {
  if (!_sentryReady) return;
  try {
    Sentry.getCurrentScope().setTag("area", area);
  } catch {
    /* swallow */
  }
}

/**
 * Tag the current Sentry scope with the user's role (NEVER name/email).
 * Pass `null` to clear.
 */
export function setSentryRole(role: string | null): void {
  if (!_sentryReady) return;
  try {
    const scope = Sentry.getCurrentScope();
    if (role) {
      scope.setTag("role", role);
    } else {
      scope.setTag("role", "anonymous");
    }
  } catch {
    /* swallow */
  }
}

/**
 * Safe exception capture that strips PII/tokens before sending.
 * Use this instead of Sentry.captureException directly.
 */
export function captureExceptionSafe(
  error: unknown,
  context?: Record<string, any>
) {
  if (!SENTRY_DSN) return;

  const cleanContext = context ? scrubObject(context) : undefined;

  Sentry.captureException(error, {
    extra: cleanContext,
  });
}
